import {useCallback, useEffect, useRef, useState} from 'react';

import {getErrorSchema} from '@/hooks/TrackingManager/utils/getErrorSchema';
import {ErrorManager, ErrorMap} from '@/utils/errors/errorManager';
import logError from '@/utils/errors/logError';

import {
  useAddTeenWithInvitationMutation,
  useNewKidMutation,
  useNewSwitchKidMutation,
} from './api';
import {
  IAddKidPayload,
  IRequestCreateKid,
  IRequestCreateSwitchKid,
  IResponseAddKid,
} from './entities';
import {useTrackers} from './trackers';

const fileName = 'useRepository add-kid';

const errorCodes = {
  EMAIL_PHONE_NOT_MATCH: 'UsersFamilyProvidedEmailPhoneDontMatch',
  PARENT_EXISTS: 'UsersFamilyProvidedEmailBelongsToParent',
  TEEN_IN_FAMILY: 'UsersFamilyTeenIsAlreadyInAFamily',
  INVALID_DATA: 'UsersHandlerValidationRequest',
} as const;

const errorMap: ErrorMap = {
  [errorCodes.EMAIL_PHONE_NOT_MATCH]: {
    TITLE: `Email or phone number are already registered`,
    DETAILS: `Please double-check the info entered. If your kid already has an account, enter their Kid ID to link the accounts.`,
  },
  [errorCodes.PARENT_EXISTS]: {
    TITLE: `Parent Account Already Exists`,
    DETAILS: `You're using a phone and email already registered in a Parent. Try again with your kid's correct info`,
  },
  [errorCodes.TEEN_IN_FAMILY]: {
    TITLE: `Kid Already in a Family`,
    DETAILS: `Oops! This email and phone number belongs to a Kid with an existing family. Try again with your kid's correct info`,
  },
  [errorCodes.INVALID_DATA]: {
    TITLE: `Check your information`,
    DETAILS: `Please review the information you entered, especially your email address, and try again.`,
  },
};

const defaultErrorAddTeenWithInvitation = {
  Summary: 'Invalid Family Code',
  Message:
    "The most common mistake? Entering a referral code or Text verification code. Make sure you're using the Kid ID.",
} as const;

const createMockAddKidResponse = (
  payload: IAddKidPayload,
): IResponseAddKid => ({
  family_id: '',
  teen: {
    uuid: payload.uuid,
    name: payload.name,
    email: payload.email,
    phone: payload.phone,
    first_name: payload.first_name,
    last_name: payload.last_name,
    date_of_birth: payload.date_of_birth,
    switch_account: payload.switch_account,

    // Mocked data
    age_verification_data: null,
    discord_data: null,
    roles: [],
    created_by_parent: false,
    fresh_chat_id: null,
    web3_address: '',
    onboarding: '',
    created_at: undefined,
    updated_at: undefined,
    bank_account_status: null,
    verified: false,
    card_created: false,
    fulfilled_intros: null,
    referral_code: undefined,
    authentication_system: '',
    invitation_code: '',
    onboarding_version: '',
    bank_info: undefined,
  },
});

const errorMapWithInvitation: ErrorMap = {
  [`UsersItemNotFound`]: {
    TITLE: defaultErrorAddTeenWithInvitation.Summary,
    DETAILS: defaultErrorAddTeenWithInvitation.Message,
  },
  [`UsersAddingMemberWithWrongRole`]: {
    TITLE: defaultErrorAddTeenWithInvitation.Summary,
    DETAILS: defaultErrorAddTeenWithInvitation.Message,
  },
  [`UsersAddingTeenIntoFamily`]: {
    TITLE: defaultErrorAddTeenWithInvitation.Summary,
    DETAILS: defaultErrorAddTeenWithInvitation.Message,
  },
  [`UsersAddingMemberExistingInOtherFamily`]: {
    TITLE: defaultErrorAddTeenWithInvitation.Summary,
    DETAILS: defaultErrorAddTeenWithInvitation.Message,
  },
};

export const useAddKidRepository = () => {
  const [
    triggerAddKidWithInvitation,
    {
      isSuccess: isAddKidWithInvitationSuccess,
      isError: isAddKidWithInvitationError,
      isLoading: isAddKidWithInvitationLoading,
    },
  ] = useAddTeenWithInvitationMutation();
  const [triggerNewKid, {isSuccess: isNewKidSuccess, isError: isNewKidError}] =
    useNewKidMutation();
  const [
    triggerNewSwitchKid,
    {isSuccess: isNewKidSwitchSuccess, isError: isNewKidSwitchError},
  ] = useNewSwitchKidMutation();
  const [skipAddKidSuccess, setSkipAddKidSuccess] = useState(false);
  const [kidCreated, setKidCreated] = useState<IResponseAddKid>(undefined);
  const kidCreatedRef = useRef<IResponseAddKid>(undefined);
  const [hasErrorDontMatch, setHasErrorDontMatch] = useState(false);
  const {
    trackApiErrorAddKid,
    trackApiSuccessAddKid,
    trackApiSkipAddKidSwitchable,
    trackApiErrorAddKidMatchExisting,
    trackApiAddKidFamilyCodeError,
    trackApiAddKidFamilyCodeSuccess,
  } = useTrackers();

  useEffect(() => {
    // Sync the ref with the current state
    kidCreatedRef.current = kidCreated;
  }, [kidCreated]);

  const isSwitchKidPayload = useCallback((payload: IRequestCreateKid) => {
    return !payload.email || !payload.phone;
  }, []);

  const handleNewSwitchKid = useCallback(
    async (payload: IRequestCreateSwitchKid) => {
      try {
        const created = await triggerNewSwitchKid(payload);
        setKidCreated(created?.data);
        if (created?.error) {
          ErrorManager.handleError(created?.error, {
            customErrorMap: errorMap,
          });
          trackApiErrorAddKid(created?.error, true);
          logError(created?.error, {
            fileName: fileName,
            functionName: 'handleNewSwitchKid',
            customTitle: 'Error adding kid without phone or email',
            data: {payload},
          });
          return;
        }
        trackApiSuccessAddKid(created?.data?.teen?.uuid, true);
      } catch (error) {
        ErrorManager.handleError(error);
        trackApiErrorAddKid(error, true);
        logError(error, {
          fileName: fileName,
          functionName: 'api error handleNewSwitchKid',
          customTitle: 'Unknown Error adding kid without phone or email',
          data: {payload},
        });
      }
    },
    [trackApiErrorAddKid, trackApiSuccessAddKid, triggerNewSwitchKid],
  );

  const handleNewFullKid = useCallback(
    async (payload: IRequestCreateKid) => {
      try {
        const created = await triggerNewKid(payload);
        setKidCreated(created?.data);
        if (created?.error) {
          const errorSchema = getErrorSchema(created?.error);
          if (errorSchema?.error?.code === errorCodes.EMAIL_PHONE_NOT_MATCH) {
            trackApiErrorAddKidMatchExisting(created?.error);
            setHasErrorDontMatch(true);
          }

          ErrorManager.handleError(created?.error, {
            customErrorMap: errorMap,
          });
          trackApiErrorAddKid(created?.error, false);
          logError(created?.error, {
            fileName: fileName,
            functionName: 'handleNewKid',
            customTitle: 'Error adding kid',
            data: {payload},
          });
          return;
        }
        trackApiSuccessAddKid(created?.data?.teen?.uuid, false);
      } catch (error) {
        ErrorManager.handleError(error);
        trackApiErrorAddKid(error, false);
        logError(error, {
          fileName: fileName,
          functionName: 'api error handleNewKid',
          customTitle: 'Unknown Error adding kid',
          data: {payload},
        });
      }
    },
    [
      trackApiErrorAddKid,
      trackApiErrorAddKidMatchExisting,
      trackApiSuccessAddKid,
      triggerNewKid,
    ],
  );

  const isSwitchableKidExist = useCallback((payload: IAddKidPayload) => {
    return (
      payload.uuid &&
      (payload.parent.phone === payload.phone || payload.switch_account)
    );
  }, []);

  const handleSwitchableKidExist = useCallback(
    (payload: IAddKidPayload) => {
      logError(
        `Skipping API call to add kid ${payload.uuid} as switchable kid exists`,
        {
          fileName: fileName,
          functionName: 'handleSwitchableKidExist',
          customTitle: 'Skipping API call to add kid as switchable kid exists',
          data: {payload},
          severity: 'info',
          extraTags: {
            kid_uuid: payload.uuid,
          },
        },
      );
      trackApiSkipAddKidSwitchable(payload.uuid);
      setKidCreated(createMockAddKidResponse(payload));
      setSkipAddKidSuccess(true);
    },
    [trackApiSkipAddKidSwitchable],
  );

  const handleNewKid = useCallback(
    async (payload: IAddKidPayload) => {
      setSkipAddKidSuccess(false);
      if (isSwitchableKidExist(payload)) {
        handleSwitchableKidExist(payload);
        return;
      }
      const isSwitch = isSwitchKidPayload(payload);
      if (isSwitch) {
        handleNewSwitchKid({
          name: payload.name,
          date_of_birth: payload.date_of_birth,
          first_name: payload.first_name,
          last_name: payload.last_name,
        });
        return;
      }
      handleNewFullKid({
        date_of_birth: payload.date_of_birth,
        email: payload.email,
        phone: payload.phone,
        name: payload.name,
        first_name: payload.first_name,
        last_name: payload.last_name,
        ...(payload.uuid && {uuid: payload.uuid}),
      });
    },
    [
      handleNewFullKid,
      handleNewSwitchKid,
      handleSwitchableKidExist,
      isSwitchKidPayload,
      isSwitchableKidExist,
    ],
  );

  const handleAddKidWithInvitation = useCallback(
    async (kidId: string): Promise<boolean> => {
      try {
        const linked = await triggerAddKidWithInvitation({
          invitation_code: kidId,
        });
        if (linked?.error) {
          trackApiAddKidFamilyCodeError(kidId, linked.error);
          ErrorManager.handleError(linked?.error, {
            customErrorMap: errorMapWithInvitation,
          });
          logError(linked?.error, {
            fileName: fileName,
            functionName: 'handleAddKidWithInvitation',
            customTitle: 'Error adding kid with invitation',
            extraTags: {
              invitation_code: kidId,
            },
          });
          return false;
        }
        trackApiAddKidFamilyCodeSuccess(kidId);
        return true;
      } catch (error) {
        trackApiAddKidFamilyCodeError(kidId, error);
        ErrorManager.handleError(error);
        logError(error, {
          fileName: fileName,
          functionName: 'api error handleAddKidWithInvitation',
          customTitle: 'Unknown Error adding kid with invitation',
          extraTags: {
            invitation_code: kidId,
          },
        });
        return false;
      }
    },
    [
      trackApiAddKidFamilyCodeError,
      trackApiAddKidFamilyCodeSuccess,
      triggerAddKidWithInvitation,
    ],
  );

  return {
    kidCreated,
    kidCreatedRef,
    isNewKidSuccess:
      isNewKidSuccess || isNewKidSwitchSuccess || skipAddKidSuccess,
    isNewKidError: isNewKidError || isNewKidSwitchError,
    handleNewKid,
    handleAddKidWithInvitation,
    isAddKidWithInvitationSuccess,
    isAddKidWithInvitationError,
    isAddKidWithInvitationLoading,
    hasErrorDontMatch,
  };
};
